/**
 * CustomerPropertyHistory → the PROPERTY TIMELINE (PROP-TL, Seth 2026-06-16).
 * Evolved from the "Owners at this address" card into the house's life story:
 * ownership handoffs AND equipment installs on one clickable, chronological
 * timeline (heating / cooling / water icons), newest first.
 *
 * Locked model (Seth, 2026-06-06): property history travels with the HOUSE
 * (equipment / what-was-serviced); person history stays with the PERSON. This
 * is the read surface; the transfer flow + merge/move admin are elsewhere.
 *
 * 34i82 (Seth 2026-10-20: "At this address and property timeline are kind of
 * showing the same info … show the exact install date if we have it … make
 * the timeline look a bit nicer"): the timeline is now the house's STORY and
 * the address card is the house's STATE (chips). A Jan-1 date prints the year
 * only and ESC's placeholder dates print nothing (installDateInfo). A year
 * gutter and a continuous rail carry the eye; owner changes are the milestones.
 * 34i83 (Seth: "display condenser and evaporator separate even if installed
 * on the same day"): one row per unit, each with its own icon (fan = condenser,
 * snowflake = coil, flame = furnace … lib/equipmentIcons), the model and what
 * it replaced on the detail line, and "sold Nov 18, 2015" when the unit was
 * charged on an earlier invoice than the one that installed it (DOUBLE-ENTRY).
 * 34i84 (Seth: "nice looking UI filters on top … if there is multiple I could
 * click air cleaner and see just what was installed … clicking on some items
 * brings out a side bar and others bring you to the Equipment tab — uniformity
 * please"): a filter for what THIS house has, and every install row lands on
 * its unit in the Equipment tab (?tab=equipment&unit=…), where the unit's
 * detail carries the install invoice and the call. No more side peek from here.
 * 34i85 (Seth: "make the filter chips look nicer … maybe somewhere in the
 * header, something innovative and clean"): the LEGEND IS THE FILTER — a quiet
 * tray of small colored icons in the card header, one per kind this house has
 * (plus the house for owners). It reads as "what's here" at a glance; tap one
 * and the timeline shows only that kind, tap again for everything. No text
 * chips, no row of pills; the readout ("Air Cleaner · 2 of 13 · Show all")
 * appears only while a filter is on.
 * 34i86 (Seth, on 1003324: "there was a little expand icon next to Ryan Pieper
 * so I clicked it expecting it to do something. Instead it removed a bunch of
 * stuff"): the bare ✕ on a from-notes owner row was the dismiss button, and
 * dismissing the only prior-owner note folds the whole owner story. Now a
 * from-notes row EXPANDS on click (the note it came from · Open Notes), the
 * removal is a two-step "Not a prior owner? Remove / Keep" inside the row,
 * and an Undo stays on the card after a removal.
 *
 * Shows whenever there is a story — an owner change, a prior owner in the
 * notes, or any dated install; multi-property accounts also get the
 * first-invoice anchor (Seth 2026-10-01: Jersey deserves its timeline).
 */
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Home, History, X, NotebookPen, ChevronRight, type LucideIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Card } from "@/components/ui/Card";
import { CardHeader } from "@/components/ui/CardHeader";
import { CustomerLink } from "@/components/links";
import {
  usePropertyHistory,
  useCustomerSignals,
  useResolveSignal,
  useOwnerVisitSplit,
  useLocationLifetimes,
  type PropertyHistoryRow,
} from "./api";
import { useCustomerEquipment, useAiEquipmentOverrides } from "./CustomerEquipment";
import { useEquipmentOverrides } from "./equipmentOverrides";
import { useLocationOwnership } from "@/features/jobs/properties";
import { buildEquipmentView, installDateInfo, installDateLabel, categoryLabelFor, type ClassifiedEquip, type EquipCategory } from "@/lib/equipment";
import { equipStyle } from "@/lib/equipmentIcons";
import { cn } from "@/lib/cn";
// HDR-DISPATCH-01: the house's other accounts (recorded links + address
// matches − undone pairs) and the unlink moved here from the 360 band.
import { useHouseAccounts, useHouseUndo } from "./houseHistory";

const COMPANY_RE =
  /\b(LLC|INC|HOMES?|CONSTRUCTION|BUILDERS?|BUILDING|DEVELOP|PROPERTIES|MGMT|MANAGEMENT|APARTMENTS?|REALTY|RENTALS?|GROUP)\b/i;

function cleanName(r: PropertyHistoryRow): string {
  if (r.locname && !/^\d/.test(r.locname)) return r.locname;
  const f = (r.fullname ?? "").replace(/\s+\d{4,}$/, "");
  const m = f.match(/^([^,]+),(.+)$/);
  // PROPERTY-LINK-01: "The Landschute Group, Inc." stays a company name.
  if (m && /^(inc|llc|llp|lp|ltd|co|corp|pllc|pc|pa)\.?$/i.test(m[2].trim())) return f.trim();
  return (m ? `${m[2].trim()} ${m[1].trim()}` : f).trim();
}
const yearOf = (d: string | null) => (d ? new Date(d).getFullYear() : null);
const normName = (s: string) =>
  s.toLowerCase().replace(/\b(llc|inc|homes?|construction|builders?|building|the)\b/g, "").replace(/[^a-z0-9]/g, "");
/** "Lennox SLP98UH135XV60D" — brand once, model once, never "LENNOX · LENNOX". */
function unitLabel(u: ClassifiedEquip): string {
  const mfg = (u.mfg ?? "").trim();
  const raw = (u.model ?? "").trim();
  // "UNKNOWN" / "N/A" in the model slot is ESC's shrug, not a model.
  const model = /^(unknown|n\/?a|none|tbd|-+|\?+)$/i.test(raw) ? "" : raw;
  // No model: the brand if the mfg slot holds one ("Carrier"), the category
  // when ESC put the TYPE there instead ("AIR COND." → Air Conditioner).
  if (!model) return mfg && !/furnace|cond|coil|heat|unit|pump|boiler|air|humid|filter|thermo|water/i.test(mfg) ? mfg : u.categoryLabel;
  if (!mfg || model.toUpperCase().startsWith(mfg.toUpperCase())) return model;
  const brand = mfg.length <= 4 ? mfg.toUpperCase() : mfg.charAt(0).toUpperCase() + mfg.slice(1).toLowerCase();
  return `${brand} ${model}`;
}

type OwnerEntry = {
  key: string; name: string; isSelf: boolean; isBuilder: boolean;
  startYear: number | null; endYear: number | null; visits: number;
  fromNotes: boolean; custno?: string; locno?: string | null;
  sourceText?: string | null; signalId?: string; meta?: string;
  /** From a RECORDED transfer (pegasus_property_ownership) — authoritative. */
  recorded?: boolean;
  /** Full end date of a recorded tenure (for "until Aug 2026" wording). */
  endDate?: string | null;
  /** HOUSE-UNDO-01: a recorded house link to another account — can be taken back here. */
  recordedLink?: boolean;
  /** The date shown on the right of the row. */
  when?: string | null;
};
type InstallEvent = {
  key: string;
  /** mirror_equipment.counter — the unit the Equipment tab opens. */
  counter: string;
  year: number;
  /** Sort key — the exact day, or Jan 1 when only the year is known. */
  iso: string;
  when: string;
  cat: EquipCategory;
  title: string;
  detail: string | null;
  /** "sold Nov 18, 2015" — charged on an earlier invoice than the install. */
  sold: string | null;
  replaced: string | null;
  invoice: string | null;
};
type TLItem =
  | { type: "owner"; year: number | null; e: OwnerEntry }
  | { type: "install"; year: number | null; ev: InstallEvent }
  /** The story's opening line: our first invoice at this property. */
  | { type: "svc"; year: number | null; date: string };

export function CustomerPropertyHistory({
  custno,
  locno,
  multiProperty,
}: {
  custno: string;
  locno: string | null;
  /** On multi-property accounts the timeline shows even for young/thin
   *  properties (anchored by the first invoice) so every property on the
   *  account reads the same way. */
  multiProperty?: boolean;
}) {
  const [, setSearchParams] = useSearchParams();
  const openUnit = (counter: string) =>
    setSearchParams((prev) => { prev.set("tab", "equipment"); prev.set("unit", counter); return prev; });
  // Filter chips: one equipment kind, or the owners, or everything (null).
  const [filter, setFilter] = useState<EquipCategory | "owners" | null>(null);
  // A from-notes owner row opens to show its note; a removal leaves an Undo behind.
  const [openOwner, setOpenOwner] = useState<string | null>(null);
  const [undoOwner, setUndoOwner] = useState<{ id: string; name: string } | null>(null);
  const openNotes = () => setSearchParams((prev) => { prev.set("tab", "notes"); return prev; });
  const { data } = usePropertyHistory(custno, locno);
  const { data: signals } = useCustomerSignals(custno);
  const { data: split } = useOwnerVisitSplit(custno, locno);
  const { data: equipment } = useCustomerEquipment(custno, locno);
  const aiOverrides = useAiEquipmentOverrides(equipment ?? []).data;
  const { data: lifecycleOverrides } = useEquipmentOverrides(custno, locno);
  const { data: owned } = useLocationOwnership(custno, locno);
  // PROP-LTV data doubles as the timeline's anchor on multi-property
  // accounts: the first invoice date = when this property's Sedgwick story
  // began. (Query is deduped with the header's — same key.)
  const { data: lifetimes } = useLocationLifetimes(multiProperty && locno ? custno : undefined);
  const lt = locno ? lifetimes?.get(locno) : undefined;
  const resolve = useResolveSignal();
  // HDR-DISPATCH-01 (the band's house line retired): house_accounts_at is the
  // authority on WHICH accounts belong to this house — it carries the links a
  // human recorded (HOUSE-MANUAL-01) and drops the pairs a human undid
  // (HOUSE-UNDO-01); the address matcher alone knows neither. So the
  // address-matched rows are kept only while that answer agrees, and a
  // hand-linked account at another spelling of the address gets its row.
  const { data: houseAccounts } = useHouseAccounts(custno, locno);
  const houseUndo = useHouseUndo();
  const houseByCustno = new Map((houseAccounts ?? []).map((a) => [a.custno, a] as const));
  const unlinkHouse = (otherCustno: string, otherName: string) => {
    if (!window.confirm(`Unlink ${otherName} from this house? Their visits and units stop showing here and the pair won't be suggested again. (Linking them again later brings it back.)`)) return;
    houseUndo.mutate({ custno, locno, otherCustno });
  };

  const rows = data ?? [];
  const addrRows = houseAccounts ? rows.filter((r) => r.is_self || houseByCustno.has(r.custno)) : rows;
  const linkedExtra = (houseAccounts ?? []).filter(
    (a) => !a.isSelf && a.source === "recorded" && !rows.some((r) => r.custno === a.custno),
  );
  const addrNames = new Set(rows.map((r) => normName(cleanName(r))));
  const isNewBuild = owned?.property?.origin === "nc-job";

  const noteOwners = (signals ?? []).filter(
    (s) =>
      s.signal_type === "prior_owner" && s.status !== "dismissed" && !!s.value &&
      (!s.locno || !locno || s.locno === locno) && !addrNames.has(normName(s.value)),
  );

  // --- OWNER entries ---
  // RECORDED transfers first (the 360's "New owner" flow) — authoritative
  // (Seth 2026-08-21: the Jetto sale wasn't showing here; recorded owners
  // were never wired into this block).
  const recordedPrior: OwnerEntry[] = (owned?.previousOwners ?? [])
    .filter((p) => p.ownership.role !== "builder")
    .map((p) => ({
      key: `r-${p.ownership.id}`, name: p.owner.name, isSelf: false,
      isBuilder: COMPANY_RE.test(p.owner.name),
      startYear: yearOf(p.ownership.start_date), endYear: yearOf(p.ownership.end_date),
      endDate: p.ownership.end_date, visits: 0, fromNotes: false, recorded: true,
    }));
  const recordedNames = new Set(recordedPrior.map((e) => normName(e.name)));
  const recordedCurrentStartDate =
    (owned?.ownership ?? []).find((r) => !r.end_date && r.role === "owner")?.start_date ?? null;
  const recordedCurrentStart = yearOf(recordedCurrentStartDate);
  // HONEST WORDING (Seth 2026-08-21): the recorded date is when WE recorded
  // the change — usually when the new owner first called — NOT the closing
  // date. Say "recorded", never "bought"/"sold", unless a note states it.
  const monthYear = (d: string | null) =>
    d ? format(parseISO(d), "MMM yyyy") : null;
  const entries: OwnerEntry[] = [
    ...recordedPrior,
    ...addrRows.map((r) => ({
      key: `a-${r.custno}`, name: cleanName(r), isSelf: r.is_self,
      isBuilder: COMPANY_RE.test(r.fullname ?? ""),
      startYear: yearOf(r.first_service), endYear: yearOf(r.last_service),
      visits: r.visit_count, fromNotes: false, custno: r.custno, locno: r.locno,
      recordedLink: !r.is_self && houseByCustno.get(r.custno)?.source === "recorded",
    })),
    ...linkedExtra.map((a) => ({
      key: `h-${a.custno}`, name: a.name, isSelf: false, isBuilder: a.isCompany,
      startYear: yearOf(a.firstService), endYear: yearOf(a.lastService),
      visits: a.visitCount, fromNotes: false, custno: a.custno, locno: a.locno,
      recordedLink: true,
    })),
    ...noteOwners.filter((s) => !recordedNames.has(normName(s.value))).map((s) => ({
      key: `n-${s.id}`, name: s.value, isSelf: false,
      isBuilder: (s.detail as { role?: string } | null)?.role === "builder" || COMPANY_RE.test(s.value),
      startYear: null, endYear: null, visits: 0, fromNotes: true,
      sourceText: s.source_text, signalId: s.id,
    })),
  ];
  const ownerStory = entries.some((e) => !e.isSelf);

  const hYear = split ? new Date(split.handoff_date).getFullYear() : null;
  const selfStart = entries.find((e) => e.isSelf)?.startYear ?? null;
  const priorKey = split ? normName(split.prior_owner) : null;
  const visitsLabel = (n: number) => `${n} visit${n === 1 ? "" : "s"} here`;
  for (const e of entries) {
    if (e.isSelf) {
      e.meta = split && hYear ? `Current owner · ${visitsLabel(split.current_visits)}` : "Current owner";
      e.when = recordedCurrentStartDate ? `since ${monthYear(recordedCurrentStartDate)}` : hYear ? `since ${hYear}` : null;
    } else if (e.recorded) {
      const until = e.endDate ? monthYear(e.endDate) : null;
      e.meta = "Previous owner";
      e.when = until ? `until ${until}` : null;
    } else if (split && hYear && priorKey && normName(e.name) === priorKey) {
      const era = selfStart && selfStart < hYear ? `${selfStart}–${hYear}` : `until ${hYear}`;
      e.meta = `Prior owner · ${visitsLabel(split.prior_visits)}${e.fromNotes ? " · from notes" : ""}`;
      e.when = era;
    } else {
      const span = e.startYear ? (e.endYear && e.endYear !== e.startYear ? `${e.startYear}–${e.endYear}` : `${e.startYear}`) : null;
      const base = e.isBuilder ? (isNewBuild ? "Sedgwick new build" : "Builder / company") : "Prior owner";
      e.meta = base + (e.visits > 0 ? ` · ${visitsLabel(e.visits)}` : "") + (e.fromNotes ? " · from notes · verify" : "") + (e.recordedLink ? " · linked" : "");
      e.when = span;
    }
  }

  // --- INSTALL rows: one per unit (a condenser and its coil are two rows even
  //     on the same day — Seth), from the same classified view the Equipment
  //     tab and the address card use, so retirements, corrections and the
  //     double-entry fold agree everywhere.
  const installEvents = useMemo<InstallEvent[]>(() => {
    if (!equipment) return [];
    const view = buildEquipmentView(equipment, aiOverrides, lifecycleOverrides ?? undefined);
    // Systems and accessories only — a row the classifier could not name
    // ("Equipment installed · G31-791") is not a story; it stays on the Equipment tab.
    const units = [...view.systems, ...view.auxiliary, ...view.replaced].filter((u) => !u.duplicateOf && u.tier !== "other");
    const out: InstallEvent[] = [];
    const seen = new Set<string>();
    for (const u of units) {
      const info = installDateInfo(u.install_date);
      if (!info) continue;
      // the same model twice on one day is one install, not two
      const k = `${u.category}|${info.exact ? info.iso : info.year}|${(u.model ?? "").toUpperCase()}`;
      if (seen.has(k)) continue;
      seen.add(k);
      // What this install took out: a retired unit that names this model as its successor.
      const gone = view.replaced.find((r) => !r.duplicateOf && r.replacedByModel && r.replacedByModel === u.model && r.counter !== u.counter && r.install_date !== u.install_date);
      const goneYear = gone ? installDateInfo(gone.install_date)?.year : null;
      out.push({
        key: `i-${u.counter}`,
        counter: u.counter,
        year: info.year,
        iso: info.exact ? info.iso : `${info.year}-01-01`,
        when: installDateLabel(u.install_date) ?? String(info.year),
        cat: u.category,
        title: `${u.categoryLabel} Installed`,
        detail: unitLabel(u),
        sold: u.soldDate && installDateLabel(u.soldDate) ? `sold ${installDateLabel(u.soldDate)}` : null,
        replaced: gone ? `replaced the ${goneYear ? `${goneYear} ` : ""}${gone.categoryLabel.toLowerCase()}` : null,
        invoice: u.invoice ?? u.soldInvoice ?? null,
      });
    }
    return out;
  }, [equipment, aiOverrides, lifecycleOverrides]);

  // The anchor: first Sedgwick invoice at this property (multi-property
  // accounts only — a single-property account's whole history IS the
  // account history, so the anchor would just restate the header).
  const svcAnchor: TLItem | null =
    multiProperty && lt?.firstDate
      ? { type: "svc", year: lt.firstYear, date: lt.firstDate }
      : null;

  if (!ownerStory && noteOwners.length === 0 && installEvents.length === 0 && !svcAnchor) return null;

  // --- Merge into one timeline, newest first (null years sink to the bottom) ---
  const ownerItems: TLItem[] = (ownerStory ? entries : []).map((e) => ({
    type: "owner" as const, e,
    year: e.isSelf ? (recordedCurrentStart ?? hYear ?? e.startYear) : (e.endYear ?? e.startYear),
  }));
  const installItems: TLItem[] = installEvents.map((ev) => ({ type: "install" as const, ev, year: ev.year }));
  // Newest first; within the same year the story reads current owner (moved
  // in) -> previous owner (moved out) -> installs (by day, newest first) ->
  // and the first-invoice anchor reads last, because it's where the story starts.
  const rank = (it: TLItem) => (it.type === "owner" ? (it.e.isSelf ? 2 : 1) : it.type === "install" ? 0 : -1);
  const sub = (it: TLItem) => (it.type === "install" ? it.ev.iso : "");
  // Same day: the system first (furnace, condenser), then its coil, then accessories.
  const CAT_RANK: EquipCategory[] = ["furnace", "boiler", "heat-pump", "ac", "mini-split", "rooftop", "magic-pak", "air-handler", "garage-heater", "coil", "water-heater", "humidifier", "dehumidifier", "air-cleaner", "uv", "erv-hrv", "thermostat", "rds", "other", "consumable", "material"];
  const catRank = (it: TLItem) => (it.type === "install" ? CAT_RANK.indexOf(it.ev.cat) : 0);
  const everything = [...ownerItems, ...installItems, ...(svcAnchor ? [svcAnchor] : [])].sort(
    (a, b) => ((b.year ?? -Infinity) - (a.year ?? -Infinity)) || (rank(b) - rank(a)) || sub(b).localeCompare(sub(a)) || (catRank(a) - catRank(b)),
  );
  // The chips: one per kind this house has (in system order), Owners when
  // there is an owner story. Shown only when there is something to choose
  // between — two kinds, or a kind and the owners.
  const kindCounts = new Map<EquipCategory, number>();
  for (const ev of installEvents) kindCounts.set(ev.cat, (kindCounts.get(ev.cat) ?? 0) + 1);
  const kinds = [...kindCounts.keys()].sort((a, b) => CAT_RANK.indexOf(a) - CAT_RANK.indexOf(b));
  const chipCount = kinds.length + (ownerItems.length > 0 ? 1 : 0);
  const showChips = chipCount >= 2;
  const active = showChips ? filter : null;
  const timeline = everything.filter((it) =>
    active === null ? true
    : active === "owners" ? it.type === "owner"
    : it.type === "install" && it.ev.cat === active,
  );
  const activeKind = active && active !== "owners" ? equipStyle(active) : null;

  return (
    <>
    <Card className="overflow-hidden">
      <CardHeader
        icon={History}
        title="Property Timeline"
        count={everything.length > 0 ? everything.length : null}
        right={(isNewBuild || showChips) ? (
          <span className="flex min-w-0 items-center gap-2">
            {isNewBuild && (
              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                <Building2 className="h-3 w-3" /> Sedgwick new build
              </span>
            )}
            {showChips && (
              <span
                role="group"
                aria-label="Show only"
                className="flex max-w-[min(60vw,320px)] items-center gap-px overflow-x-auto rounded-lg bg-slate-100/90 p-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {ownerItems.length > 0 && (
                  <LegendKey
                    Icon={Home}
                    label="Owners"
                    count={ownerItems.length}
                    ink="text-brand-600"
                    on={active === "owners"}
                    onClick={() => setFilter(active === "owners" ? null : "owners")}
                  />
                )}
                {kinds.map((k) => {
                  const { Icon, ink } = equipStyle(k);
                  return (
                    <LegendKey
                      key={k}
                      Icon={Icon}
                      label={categoryLabelFor(k)}
                      count={kindCounts.get(k) ?? 0}
                      ink={ink}
                      on={active === k}
                      onClick={() => setFilter(active === k ? null : k)}
                    />
                  );
                })}
              </span>
            )}
          </span>
        ) : undefined}
      />

      {active !== null && (
        <div className="flex items-center gap-2 border-b border-hairline/70 bg-slate-50/60 px-4 py-1.5 text-[12px]">
          {active === "owners" ? (
            <Home className="h-3.5 w-3.5 text-brand-600" strokeWidth={2} />
          ) : activeKind ? (
            <activeKind.Icon className={cn("h-3.5 w-3.5", activeKind.ink)} strokeWidth={2} />
          ) : null}
          <span className="font-semibold text-slate-800">{active === "owners" ? "Owners" : categoryLabelFor(active)}</span>
          <span className="tabular-nums text-slate-400">· {timeline.length} of {everything.length}</span>
          <button
            type="button"
            onClick={() => setFilter(null)}
            className="ml-auto inline-flex items-center gap-1 rounded text-[12px] font-semibold text-brand-700 hover:text-brand-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
          >
            Show all <X className="h-3 w-3" />
          </button>
        </div>
      )}

      <div className="px-3 pb-2 pt-1 sm:px-4">
        {/* The rail runs behind the nodes: year gutter (40px) · gap · node column (28px). */}
        <ol className="relative before:absolute before:bottom-5 before:left-[63px] before:top-5 before:w-px before:bg-gradient-to-b before:from-slate-200 before:via-slate-200 before:to-transparent">
          {timeline.map((it, i) => {
            const prevYear = i > 0 ? timeline[i - 1].year : undefined;
            const showYear = it.year != null && it.year !== prevYear;
            const yearLabel = showYear ? String(it.year) : null;
            if (it.type === "owner") {
              return (
                <OwnerRow
                  key={it.e.key}
                  e={it.e}
                  yearLabel={yearLabel}
                  open={openOwner === it.e.key}
                  onToggle={it.e.fromNotes ? () => setOpenOwner((k) => (k === it.e.key ? null : it.e.key)) : undefined}
                  onOpenNotes={openNotes}
                  onDismiss={(id) => resolve.mutate({ id, status: "dismissed" }, { onSuccess: () => { setUndoOwner({ id, name: it.e.name }); setOpenOwner(null); } })}
                  onUnlink={it.e.recordedLink && it.e.custno ? () => unlinkHouse(it.e.custno as string, it.e.name) : undefined}
                  unlinking={houseUndo.isPending}
                />
              );
            }
            if (it.type === "install") {
              return (
                <InstallRow
                  key={it.ev.key}
                  ev={it.ev}
                  yearLabel={yearLabel}
                  onOpen={() => openUnit(it.ev.counter)}
                />
              );
            }
            return <SvcAnchorRow key="svc-anchor" date={it.date} yearLabel={yearLabel} />;
          })}
        </ol>

        {undoOwner && (
          <div className="mt-1 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-1.5 text-[12px] text-amber-900">
            <span className="min-w-0 truncate">Removed <b>{undoOwner.name}</b> as a prior owner</span>
            <button
              type="button"
              onClick={() => resolve.mutate({ id: undoOwner.id, status: "proposed" }, { onSuccess: () => setUndoOwner(null) })}
              className="ml-auto shrink-0 font-semibold text-brand-700 hover:text-brand-900"
            >
              Undo
            </button>
            <button type="button" onClick={() => setUndoOwner(null)} aria-label="Close" className="shrink-0 text-amber-700/60 hover:text-amber-900"><X className="h-3.5 w-3.5" /></button>
          </div>
        )}
        {/* DESIGN-CALM-01: the explanatory paragraph came off; the one fact a
            dispatcher needs — a prior owner's history may be mixed in — rides
            on the PriorOwnerBadge hover where it applies. */}
        {ownerStory && active !== "owners" && !(active && kinds.includes(active as EquipCategory)) && (
          <p className="mt-1 border-t border-hairline pt-2 text-[11px] text-slate-400">Earlier entries may belong to the previous owner.</p>
        )}
      </div>
    </Card>
    </>
  );
}

/** One key in the legend tray: a 28px tile, the kind's icon in its family
 *  color; the pressed one sits on a white tile with a hairline ring. The name
 *  and count ride the tooltip and the readout line, not the tile. */
function LegendKey({ Icon, label, count, ink, on, onClick }: {
  Icon: LucideIcon; label: string; count: number; ink: string; on: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      aria-label={`${label} · ${count}`}
      title={`${label} · ${count}`}
      className={cn(
        "grid h-7 w-7 shrink-0 place-items-center rounded-md transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
        on ? "bg-white shadow-e1 ring-1 ring-slate-900/10" : "hover:bg-white/70",
      )}
    >
      <Icon className={cn("h-4 w-4 transition-opacity", ink, on ? "opacity-100" : "opacity-80")} strokeWidth={2} />
    </button>
  );
}

/* ── Rows ──────────────────────────────────────────────────────────────────
   One grid for every row — [year gutter] [node] [content] — so the nodes line
   up on the rail whatever the row holds. The year prints once per year, on the
   first row of that year. */
const ROW = "relative grid grid-cols-[40px_28px_minmax(0,1fr)] items-start gap-x-2.5 py-2";
function YearCell({ label }: { label: string | null }) {
  return (
    <span className="pt-[5px] text-right text-[11px] font-bold tabular-nums tracking-tight text-slate-400">{label ?? ""}</span>
  );
}
function Node({ Icon, className, ring = true }: { Icon: LucideIcon; className: string; ring?: boolean }) {
  return (
    <span className={cn("relative z-10 flex h-7 w-7 items-center justify-center rounded-full ring-4 ring-white", ring && "shadow-[0_0_0_1px_rgba(15,23,42,0.06)]", className)}>
      <Icon className="h-3.5 w-3.5" strokeWidth={2} />
    </span>
  );
}
function When({ children }: { children: React.ReactNode }) {
  return <span className="shrink-0 pt-px text-[11px] tabular-nums text-slate-400">{children}</span>;
}

function OwnerRow({ e, yearLabel, open, onToggle, onOpenNotes, onDismiss, onUnlink, unlinking }: {
  e: OwnerEntry; yearLabel: string | null;
  /** From-notes rows open to show the note they came from. */
  open?: boolean; onToggle?: () => void; onOpenNotes?: () => void;
  onDismiss: (id: string) => void; onUnlink?: () => void; unlinking?: boolean;
}) {
  const Icon = e.isBuilder ? Building2 : Home;
  const [arming, setArming] = useState(false);
  const expandable = !!onToggle;
  return (
    <li className={ROW}>
      <YearCell label={yearLabel} />
      <Node
        Icon={Icon}
        className={cn(
          e.isSelf ? "bg-brand-600 text-white"
            : e.isBuilder ? "bg-emerald-50 text-emerald-600"
            : e.fromNotes ? "border border-dashed border-slate-300 bg-white text-slate-400"
            : "bg-slate-100 text-slate-500",
        )}
      />
      <div className="min-w-0">
        <div
          className={cn("flex items-baseline justify-between gap-3", expandable && "-mx-1.5 -my-1 cursor-pointer rounded-lg px-1.5 py-1 transition-colors hover:bg-slate-50")}
          role={expandable ? "button" : undefined}
          tabIndex={expandable ? 0 : undefined}
          aria-expanded={expandable ? !!open : undefined}
          onClick={expandable ? onToggle : undefined}
          onKeyDown={expandable ? (ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); onToggle?.(); } } : undefined}
        >
          <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5">
            {e.isSelf ? (
              <span className="truncate text-[13px] font-semibold text-slate-900">{e.name}</span>
            ) : e.custno ? (
              <CustomerLink custno={e.custno} locno={e.locno} className="truncate text-[13px] font-semibold">{e.name}</CustomerLink>
            ) : (
              <span className="truncate text-[13px] font-semibold text-slate-700">{e.name}</span>
            )}
            {e.fromNotes && (
              <span className="inline-flex items-center gap-0.5 rounded bg-brand-50 px-1 py-px text-[10px] font-semibold uppercase tracking-wide text-brand-500">
                <NotebookPen className="h-2.5 w-2.5" /> From notes
              </span>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-1.5">
            {e.when && (
              <span title={e.isSelf || e.recorded ? "The date we recorded the change — usually the new owner's first call, not the closing date" : undefined}>
                <When>{e.when}</When>
              </span>
            )}
            {expandable && <ChevronRight className={cn("h-3.5 w-3.5 text-slate-300 transition-transform", open && "rotate-90")} />}
          </span>
        </div>
        <div className="mt-0.5 text-[12px] leading-snug text-slate-500">
          {e.meta}
          {onUnlink && (
            <button
              type="button"
              disabled={unlinking}
              onClick={onUnlink}
              title="Not the same house? Take this link back"
              className="ml-1.5 text-[10.5px] text-slate-400 underline-offset-2 hover:text-rose-600 hover:underline disabled:opacity-50"
            >
              unlink
            </button>
          )}
        </div>
        {open && e.fromNotes && (
          <div className="mt-1.5 rounded-lg border border-hairline bg-white px-2.5 py-2 text-[12px] leading-snug text-slate-600">
            <div className="flex items-start gap-1.5">
              <NotebookPen className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
              <span className="min-w-0">
                {e.sourceText ? <i>“{e.sourceText}”</i> : "Found in the notes."}
                {onOpenNotes && (
                  <button type="button" onClick={onOpenNotes} className="ml-1.5 font-semibold text-brand-700 hover:underline">Open Notes</button>
                )}
              </span>
            </div>
            {e.signalId && (
              <div className="mt-2 flex items-center justify-end gap-2 border-t border-hairline/70 pt-2">
                {arming ? (
                  <>
                    <span className="mr-auto text-[11.5px] text-slate-500">Not a prior owner?</span>
                    <button type="button" onClick={() => setArming(false)} className="rounded-md border border-hairline px-2 py-1 text-[11.5px] font-semibold text-slate-600 hover:bg-slate-50">Keep</button>
                    <button type="button" onClick={() => { setArming(false); onDismiss(e.signalId as string); }} className="rounded-md bg-rose-600 px-2 py-1 text-[11.5px] font-semibold text-white hover:bg-rose-700">Remove</button>
                  </>
                ) : (
                  <button type="button" onClick={() => setArming(true)} className="text-[11.5px] font-semibold text-slate-500 hover:text-rose-700">Not Right…</button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/** The timeline's opening line on multi-property accounts: when Sedgwick's
 *  story at THIS property began (the first invoice here). Not clickable —
 *  it's a milestone, not a record. */
function SvcAnchorRow({ date, yearLabel }: { date: string; yearLabel: string | null }) {
  let when = date.slice(0, 4);
  try { when = format(parseISO(date), "MMM d, yyyy"); } catch { /* keep year */ }
  return (
    <li className={ROW}>
      <YearCell label={yearLabel} />
      <Node Icon={History} className="bg-brand-50 text-brand-600" />
      <div className="min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[13px] font-semibold text-slate-800">Sedgwick Service Begins</span>
          <When>{when}</When>
        </div>
        <div className="mt-0.5 text-[12px] text-slate-500">First invoice on record at this property</div>
      </div>
    </li>
  );
}

function InstallRow({ ev, yearLabel, onOpen }: { ev: InstallEvent; yearLabel: string | null; onOpen: () => void }) {
  const { Icon, node } = equipStyle(ev.cat);
  return (
    <li className={ROW}>
      <YearCell label={yearLabel} />
      <Node Icon={Icon} className={node} />
      <button
        type="button"
        onClick={onOpen}
        title="Open this unit on the Equipment tab"
        className="group -my-1 -mr-1 min-w-0 rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="truncate text-[13px] font-semibold text-slate-800">{ev.title}</span>
          <span className="flex shrink-0 items-center gap-1">
            <When>{ev.when}</When>
            <ChevronRight className="h-3.5 w-3.5 text-slate-300 transition-colors group-hover:text-slate-500" />
          </span>
        </div>
        {(ev.detail || ev.sold || ev.replaced) && (
          <div className="mt-0.5 truncate text-[12px] leading-snug text-slate-500">
            {ev.detail}
            {ev.sold && <span className="text-slate-400" title="Charged on an earlier invoice, installed later — one unit, two invoices">{ev.detail ? " · " : ""}{ev.sold}</span>}
            {ev.replaced && <span className="text-slate-400">{ev.detail || ev.sold ? " · " : ""}{ev.replaced}</span>}
          </div>
        )}
      </button>
    </li>
  );
}
